import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { getAudioFile, AudioFile } from '../services/api';

interface AudioPlayerContextType {
  // Current track
  currentTrack: AudioFile | null;

  // Playback state
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;

  // Loading/processing state
  isLoading: boolean;
  isProcessing: boolean;
  error: string | null;
  isFileNotFound: boolean;
  /**
   * Id of the track a loadTrack() call is currently resolving. Set synchronously
   * on click — BEFORE the metadata fetch — so a clicked row can highlight/show a
   * spinner instantly (Doherty threshold) instead of waiting for the network
   * round-trip that sets `currentTrack`. Cleared when the load settles.
   */
  loadingTrackId: string | null;

  // Autoplay blocked state
  needsUserInteraction: boolean;

  // Waveform data (generated once per track)
  waveformData: number[] | null;
  isGeneratingWaveform: boolean;

  // Control functions
  loadTrack: (fileId: string, autoplay?: boolean, forceReload?: boolean) => Promise<void>;
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  closePlayer: () => void;
  /**
   * Merge a partial metadata update into `currentTrack` without touching the
   * audio element. Use after server-side mutations (rename, artwork change) so
   * the UI reflects the new state without forceReload tearing down playback.
   */
  patchCurrentTrack: (patch: Partial<AudioFile>) => void;

  // Audio element ref (for advanced usage like waveform)
  audioRef: React.RefObject<HTMLAudioElement>;

  /**
   * Live frequency spectrum of the currently-playing audio (0..255 per bin), or
   * null if the realtime analyser isn't wired yet (no gesture/play has happened).
   * The returned buffer is reused across calls — read it immediately, don't keep
   * a reference. Backs audio-reactive visualizers.
   */
  getFrequencies: () => Uint8Array | null;

  // ---- Playlist / queue ----
  /** Ordered file ids of the active playlist (empty when none). */
  queue: string[];
  /** Index of the playing track within `queue`, or -1 when no queue is active. */
  queueIndex: number;
  /** True when there's a track after the current one in the queue. */
  hasNext: boolean;
  /** True when there's a track before the current one in the queue. */
  hasPrevious: boolean;
  /** Start a playlist: load `fileIds[startIndex]` and auto-advance through the rest. */
  playQueue: (fileIds: string[], startIndex?: number, collectionArtworkUrl?: string | null) => void;
  /** Skip to the next queued track (no-op at the end). */
  playNext: () => void;
  /** Skip to the previous queued track (no-op at the start). */
  playPrevious: () => void;
  /** Drop the active playlist (playback continues; it just won't auto-advance). */
  clearQueue: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

// Mobile platforms (iOS Safari, Android Chrome) suspend the AudioContext when the
// page backgrounds or the screen locks. Routing the playback <audio> through a
// MediaElementSource there silently kills lockscreen / background playback — the
// floating player's signature feature. So on mobile we DON'T wire the realtime
// analyser at all: the audio stays straight from the <audio> element to the
// hardware (background playback preserved), and the visualizer falls back to its
// idle state (getFrequencies() → null, which AudioReactiveBars already handles).
// Desktop has no such suspension, so it keeps the real FFT.
const REALTIME_ANALYSER_SAFE = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1); // iPadOS 13+ masquerades as Mac
  const android = /Android/.test(ua);
  return !(iOS || android);
})();

// Build the now-playing artwork list for a track. Priority: the track's own
// 320² square thumbnail → its full-size cover (older artwork has no thumb) → the
// collection cover, when playing inside a collection "Play all" queue → the
// opaque brand icon. Used by both the synchronous play()-time set and the
// re-assert effect. (Renders on every MediaSession-honouring browser — iOS Safari
// tabs ignore lock-screen artwork regardless.)
function buildArtwork(track: AudioFile, collectionArtworkUrl?: string | null): MediaImage[] {
  if (track.artworkThumbUrl) {
    return [{ src: track.artworkThumbUrl, sizes: '320x320', type: 'image/jpeg' }];
  }
  if (track.artworkUrl) {
    return [{ src: track.artworkUrl, sizes: 'any', type: 'image/jpeg' }];
  }
  if (collectionArtworkUrl) {
    return [{ src: collectionArtworkUrl, sizes: 'any', type: 'image/jpeg' }];
  }
  return [{ src: '/lockscreen-icon.png', sizes: '128x128', type: 'image/png' }];
}

export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentTrackIdRef = useRef<string | null>(null);
  const isProcessingRef = useRef<boolean>(false);

  // Track state
  const [currentTrack, setCurrentTrack] = useState<AudioFile | null>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFileNotFound, setIsFileNotFound] = useState(false);
  const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
  const [loadingTrackId, setLoadingTrackId] = useState<string | null>(null);

  // Playlist / queue. Refs back the live values read inside the stable `ended`
  // handler (same reason currentTrackIdRef exists); state mirrors them for the UI.
  const [queue, setQueue] = useState<string[]>([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const queueRef = useRef<string[]>([]);
  const queueIndexRef = useRef(-1);
  // Set true by playQueue / auto-advance so the loadTrack they trigger keeps the
  // queue; ANY other loadTrack (a manual single-track play) clears it.
  const playingFromQueueRef = useRef(false);
  // Collection cover for the active "Play all" queue — used as a lock-screen
  // artwork fallback for queued tracks that have no art of their own.
  const collectionArtworkRef = useRef<string | null>(null);

  // Waveform state
  const [waveformData, setWaveformData] = useState<number[] | null>(null);
  const [isGeneratingWaveform, setIsGeneratingWaveform] = useState(false);

  // Polling interval ref
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Realtime-analyser graph (Tier 2 reactive visualizer). Built ONCE, lazily, on
  // the first user-gesture play — createMediaElementSource is once-per-element and
  // re-routes audio through the graph, so it must connect through to destination.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const freqDataRef = useRef<Uint8Array | null>(null);

  const ensureAnalyser = useCallback(() => {
    // On mobile, never reroute the playback element through Web Audio — it would
    // break background/lockscreen playback (see REALTIME_ANALYSER_SAFE above).
    if (!REALTIME_ANALYSER_SAFE) return;
    if (!audioRef.current) return;
    if (!audioCtxRef.current) {
      try {
        const Ctx = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const source = ctx.createMediaElementSource(audioRef.current);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128; // 64 bins — plenty for a compact bar visualizer
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyser.connect(ctx.destination); // MUST reach destination or audio dies
        audioCtxRef.current = ctx;
        sourceNodeRef.current = source;
        analyserRef.current = analyser;
        freqDataRef.current = new Uint8Array(analyser.frequencyBinCount);
      } catch (e) {
        // A failure here must never break playback — the visualizer just stays off.
        console.warn('[AudioPlayer] analyser init failed (visualizer disabled):', e);
      }
    }
    // iOS/Safari start suspended; resume from the gesture that called us.
    if (audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
  }, []);

  const getFrequencies = useCallback((): Uint8Array | null => {
    const analyser = analyserRef.current;
    const buf = freqDataRef.current;
    if (!analyser || !buf) return null;
    analyser.getByteFrequencyData(buf);
    return buf;
  }, []);

  // Set the now-playing metadata SYNCHRONOUSLY at play() time. iOS snapshots the
  // lock-screen widget the instant playback starts and does NOT refresh it from a
  // later React effect — which is why the widget showed up with working controls
  // but blank title/artist/artwork. Call this right before audioRef.play() so the
  // metadata exists when the OS builds the widget. (The effect below still
  // re-asserts it for in-app metadata edits / track changes.)
  const applyMediaMetadata = useCallback((track: AudioFile | null) => {
    if (!('mediaSession' in navigator) || !track) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title || track.filename || 'PasteWaves',
      artist: track.ownerName || 'PasteWaves',
      artwork: buildArtwork(track, collectionArtworkRef.current),
    });
  }, []);

  // Generate waveform from audio URL
  const generateWaveform = useCallback(async (audioUrl: string) => {
    setIsGeneratingWaveform(true);

    try {
      // Try to load pre-generated CSV waveform data first
      // Extract fileId from audio URL (format: https://.../uploads/FILE_ID/audio.mp3)
      const fileIdMatch = audioUrl.match(/\/uploads\/([a-f0-9-]+)\//);

      if (fileIdMatch) {
        const csvUrl = audioUrl.replace('/audio.mp3', '/waveform.csv');

        console.log('[Waveform] Attempting to load pre-generated CSV:', csvUrl);

        try {
          const csvResponse = await fetch(csvUrl, {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });

          if (csvResponse.ok) {
            const csvText = await csvResponse.text();
            const lines = csvText.trim().split('\n');

            // Skip header line
            const dataLines = lines.slice(1);
            const amplitudes: number[] = [];

            for (const line of dataLines) {
              const parts = line.split(',');
              if (parts.length >= 2) {
                const amplitude = parseFloat(parts[1]);
                if (!isNaN(amplitude)) {
                  amplitudes.push(amplitude);
                }
              }
            }

            if (amplitudes.length > 0) {
              console.log('[Waveform] Successfully loaded CSV waveform data:', amplitudes.length, 'samples');

              // Normalize the data (CSV already has RMS values, but normalize to max=1)
              const max = Math.max(...amplitudes);
              const normalizedData = max > 0 ? amplitudes.map(val => val / max) : amplitudes;

              setWaveformData(normalizedData);
              setIsGeneratingWaveform(false);
              return; // Success - exit early
            }
          }

          console.log('[Waveform] CSV not available or empty, falling back to Web Audio API');
        } catch (csvError) {
          console.log('[Waveform] Error loading CSV, falling back to Web Audio API:', csvError);
        }
      }

      // Fallback: Generate waveform using Web Audio API (original method)
      console.log('[Waveform] Generating waveform using Web Audio API');

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Add cache-busting and no-cache headers to ensure fresh data
      const response = await fetch(audioUrl, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const rawData = audioBuffer.getChannelData(0);
      const samples = 200; // Number of bars in waveform
      const blockSize = Math.floor(rawData.length / samples);
      const filteredData: number[] = [];

      for (let i = 0; i < samples; i++) {
        let blockStart = blockSize * i;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[blockStart + j]);
        }
        filteredData.push(sum / blockSize);
      }

      // Normalize the data
      const max = Math.max(...filteredData);
      const normalizedData = filteredData.map(val => val / max);

      setWaveformData(normalizedData);

      // Close the AudioContext to free up resources
      await audioContext.close();
    } catch (error) {
      console.error('Error generating waveform:', error);
      // Don't fail the whole track load if waveform fails
      setWaveformData(null);
    } finally {
      setIsGeneratingWaveform(false);
    }
  }, []);

  // Load track with metadata fetching and processing status polling
  const loadTrack = useCallback(async (fileId: string, autoplay: boolean = false, forceReload: boolean = false) => {
    // A direct single-track play ends any active playlist (and its collection
    // cover). playQueue/auto-advance set playingFromQueueRef first so their
    // loadTrack keeps the queue.
    if (!playingFromQueueRef.current) {
      collectionArtworkRef.current = null;
      if (queueRef.current.length) {
        queueRef.current = [];
        queueIndexRef.current = -1;
        setQueue([]);
        setQueueIndex(-1);
      }
    }
    playingFromQueueRef.current = false;

    // Guard: Skip if already loaded, audio source is set, AND track is ready
    // This prevents restart on expand/navigate but allows initial loads
    // forceReload bypasses this guard completely
    const alreadyLoaded =
      !forceReload &&
      currentTrackIdRef.current === fileId &&
      audioRef.current?.src &&
      audioRef.current?.src.includes(fileId);

    if (alreadyLoaded) {
      console.log('Track already loaded, skipping reload:', fileId);
      // If autoplay requested but not currently playing, start playback
      if (autoplay && audioRef.current && audioRef.current.paused) {
        audioRef.current.play().catch(err => {
          if (err.name !== 'AbortError') {
            console.error('Autoplay error:', err);
          }
        });
      }
      return;
    }

    console.log('Loading track:', fileId, forceReload ? '(forced)' : '');

    // Synchronous, pre-fetch optimistic signal so a clicked row reacts instantly.
    setLoadingTrackId(fileId);
    // loadTrack is invoked from the click gesture — wire/resume the analyser now,
    // while we still have gesture context (the actual audio.play() happens later
    // in an async canplay handler that no longer counts as a user gesture).
    ensureAnalyser();

    try {
      setIsLoading(true);
      setError(null);
      setIsFileNotFound(false);

      // Clear any existing polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      // Pause current playback only if loading a different track
      if (audioRef.current && currentTrackIdRef.current !== fileId) {
        audioRef.current.pause();
        setIsPlaying(false);
        // Clear waveform when switching tracks
        setWaveformData(null);
      }

      // Update the current track ID ref
      currentTrackIdRef.current = fileId;

      // Fetch file metadata
      const fileData = await getAudioFile(fileId);

      // Default to 'pending' if processingStatus is missing OR if URL is missing
      // A file marked 'completed' without a URL is actually still processing
      const processingStatus = !fileData.processingStatus || !fileData.url ? 'pending' : fileData.processingStatus;

      console.log('[AudioPlayer] File loaded:', {
        fileId,
        processingStatus,
        rawStatus: fileData.processingStatus,
        hasUrl: !!fileData.url,
        title: fileData.title
      });

      if (processingStatus === 'pending' || processingStatus === 'processing') {
        setIsProcessing(true);
        isProcessingRef.current = true;
        setCurrentTrack(fileData);

        // Clear audio src to prevent error events while processing
        if (audioRef.current) {
          audioRef.current.src = '';
        }

        // Start polling for processing completion
        let retryCount = 0;
        const maxRetries = 60; // Increased from 30 to 60 (2 minutes total)
        let hasCompletedProcessing = false; // Flag to prevent duplicate processing

        pollingIntervalRef.current = setInterval(async () => {
          try {
            // Skip if we've already handled completion
            if (hasCompletedProcessing) {
              return;
            }

            const updatedData = await getAudioFile(fileId);

            const updatedStatus = !updatedData.processingStatus || !updatedData.url ? 'pending' : updatedData.processingStatus;

            if (updatedStatus === 'completed' && updatedData.url) {
              // Set flag IMMEDIATELY to prevent duplicate processing
              hasCompletedProcessing = true;
              console.log('[AudioPlayer] Processing completed, loading audio');

              // Clear interval
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }

              setCurrentTrack(updatedData);
              setIsProcessing(false);
              isProcessingRef.current = false;

              // Load audio with proper event handling
              if (audioRef.current) {
                const audio = audioRef.current;

                // Handler for when audio is ready to play
                const handleCanPlay = () => {
                  console.log('[AudioPlayer] Audio ready to play');
                  audio.removeEventListener('canplaythrough', handleCanPlay);
                  audio.removeEventListener('error', handleLoadError);

                  // Autoplay if requested
                  if (autoplay) {
                    console.log('[AudioPlayer] Attempting autoplay...');
                    audio.play().catch(err => {
                      if (err.name === 'NotAllowedError') {
                        // Autoplay blocked by browser - show "Click to Play" UI
                        console.log('[AudioPlayer] Autoplay blocked - user interaction required');
                        setNeedsUserInteraction(true);
                      } else if (err.name !== 'AbortError') {
                        console.error('Autoplay error:', err);
                      }
                    });
                  }
                };

                // Handler for load errors
                const handleLoadError = (e: Event) => {
                  console.error('[AudioPlayer] Failed to load audio after processing', e);
                  audio.removeEventListener('canplaythrough', handleCanPlay);
                  audio.removeEventListener('error', handleLoadError);
                  setError('Audio file is not ready yet. Please wait a moment and try again.');
                };

                // Set up event listeners before loading
                audio.addEventListener('canplaythrough', handleCanPlay, { once: true });
                audio.addEventListener('error', handleLoadError, { once: true });

                // Set source and load
                audio.src = updatedData.url;
                console.log('[AudioPlayer] Loading audio from:', updatedData.url);
                audio.load();

                // Generate waveform in parallel
                generateWaveform(updatedData.url);
              }
            } else if (updatedStatus === 'failed') {
              // Set flag to prevent duplicate error handling
              hasCompletedProcessing = true;
              setError('Audio processing failed. Please try uploading again.');
              setIsProcessing(false);
              isProcessingRef.current = false;

              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            }

            retryCount++;
            if (retryCount >= maxRetries) {
              // Set flag to prevent duplicate timeout handling
              hasCompletedProcessing = true;
              setError('Audio processing is taking longer than expected. Please refresh the page later.');
              setIsProcessing(false);
              isProcessingRef.current = false;

              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            }
          } catch (err) {
            console.error('Error polling for file status:', err);
          }
        }, 2000);

      } else if (processingStatus === 'completed' && fileData.url) {
        setCurrentTrack(fileData);
        setIsProcessing(false);
        isProcessingRef.current = false;

        // Load audio with proper event handling
        if (audioRef.current) {
          const audio = audioRef.current;
          console.log('[AudioPlayer] Loading completed file:', fileData.url);

          // Handler for when audio is ready to play
          const handleCanPlay = () => {
            console.log('[AudioPlayer] Audio ready to play');
            audio.removeEventListener('canplaythrough', handleCanPlay);
            audio.removeEventListener('error', handleLoadError);

            // Autoplay if requested
            if (autoplay) {
              console.log('[AudioPlayer] Attempting autoplay...');
              applyMediaMetadata(fileData); // before play() so iOS snapshots it
              audio.play().catch(err => {
                if (err.name !== 'AbortError') {
                  console.error('Autoplay error:', err);
                  // Set flag to show "Click to Play" UI
                  setNeedsUserInteraction(true);
                }
              });
            }
          };

          // Handler for load errors
          const handleLoadError = (e: Event) => {
            console.error('[AudioPlayer] Failed to load completed file', e);
            audio.removeEventListener('canplaythrough', handleCanPlay);
            audio.removeEventListener('error', handleLoadError);
            setError('Failed to load audio file. Please try again.');
          };

          // Set up event listeners before loading
          audio.addEventListener('canplaythrough', handleCanPlay, { once: true });
          audio.addEventListener('error', handleLoadError, { once: true });

          // Set source and load
          audio.src = fileData.url;
          audio.load();

          // Generate waveform in parallel
          generateWaveform(fileData.url);
        }
      } else if (processingStatus === 'failed') {
        setError('Audio processing failed. Please try uploading again.');
        setIsProcessing(false);
        isProcessingRef.current = false;
      }

    } catch (err) {
      console.error('Error loading track:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load audio file';

      // Check if it's a 404 error
      if (errorMessage === 'FILE_NOT_FOUND') {
        setIsFileNotFound(true);
        setError('File not found');
      } else {
        setIsFileNotFound(false);
        setError(errorMessage);
      }

      setIsProcessing(false);
      isProcessingRef.current = false;
    } finally {
      setIsLoading(false);
      // By here currentTrack already reflects this fileId (set inside try) or the
      // load errored — either way the optimistic signal has done its job.
      setLoadingTrackId(null);
    }
  }, [generateWaveform, ensureAnalyser]);

  // ---- Playlist / queue ----------------------------------------------------
  const playAtQueueIndex = useCallback((index: number) => {
    const q = queueRef.current;
    if (index < 0 || index >= q.length) return;
    queueIndexRef.current = index;
    setQueueIndex(index);
    playingFromQueueRef.current = true; // keep the queue through this load
    loadTrack(q[index], true, true);
  }, [loadTrack]);

  const playQueue = useCallback((fileIds: string[], startIndex: number = 0, collectionArtworkUrl?: string | null) => {
    const ids = fileIds.filter(Boolean);
    if (!ids.length) return;
    const start = Math.min(Math.max(startIndex, 0), ids.length - 1);
    queueRef.current = ids;
    queueIndexRef.current = start;
    setQueue(ids);
    setQueueIndex(start);
    collectionArtworkRef.current = collectionArtworkUrl ?? null;
    playingFromQueueRef.current = true;
    loadTrack(ids[start], true, true);
  }, [loadTrack]);

  const playNext = useCallback(() => {
    playAtQueueIndex(queueIndexRef.current + 1);
  }, [playAtQueueIndex]);

  const playPrevious = useCallback(() => {
    playAtQueueIndex(queueIndexRef.current - 1);
  }, [playAtQueueIndex]);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    queueIndexRef.current = -1;
    setQueue([]);
    setQueueIndex(-1);
  }, []);

  // Playback controls
  const play = useCallback(() => {
    if (audioRef.current && currentTrack?.processingStatus === 'completed') {
      // Clear the "needs interaction" flag when user manually plays
      setNeedsUserInteraction(false);

      // Wire/resume the analyser on this gesture so the visualizer can react.
      ensureAnalyser();

      applyMediaMetadata(currentTrack); // before play() so iOS snapshots it

      // Catch and ignore AbortError when play is interrupted by pause
      audioRef.current.play().catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Audio play error:', err);
        }
      });
    }
  }, [currentTrack, ensureAnalyser, applyMediaMetadata]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, []);

  const setVolume = useCallback((vol: number) => {
    const clampedVolume = Math.max(0, Math.min(1, vol));
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
    setVolumeState(clampedVolume);
  }, []);

  const closePlayer = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrentTrack(null);
    currentTrackIdRef.current = null;
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setNeedsUserInteraction(false);
    setWaveformData(null);
    setIsGeneratingWaveform(false);

    // Clear polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const patchCurrentTrack = useCallback((patch: Partial<AudioFile>) => {
    setCurrentTrack(prev => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => {
      setCurrentTime(0);
      // Auto-advance through the playlist if there's a next track; else stop.
      const q = queueRef.current;
      const i = queueIndexRef.current;
      if (q.length && i >= 0 && i < q.length - 1) {
        const nextIndex = i + 1;
        queueIndexRef.current = nextIndex;
        setQueueIndex(nextIndex);
        playingFromQueueRef.current = true;
        loadTrack(q[nextIndex], true, true);
      } else {
        setIsPlaying(false);
      }
    };
    const handleError = (e: Event) => {
      const audio = e.target as HTMLAudioElement;
      const audioError = audio.error;

      console.error('Audio playback error:', {
        event: e,
        audioError: audioError,
        errorCode: audioError?.code,
        errorMessage: audioError?.message,
        audioSrc: audio.src,
        currentTrack: currentTrackIdRef.current,
        isProcessing: isProcessingRef.current
      });

      // Don't show error if we're still processing - this is expected
      if (isProcessingRef.current) {
        console.log('[AudioPlayer] Ignoring error during processing');
        setIsPlaying(false);
        return;
      }

      // If we get a media error shortly after loading (within 5 seconds),
      // it might be that the file isn't ready yet - treat as processing
      if (audioError && currentTrackIdRef.current) {
        const errorCode = audioError.code;

        // MEDIA_ERR_SRC_NOT_SUPPORTED or MEDIA_ERR_DECODE often means the file isn't ready
        if (errorCode === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED ||
            errorCode === MediaError.MEDIA_ERR_DECODE) {
          console.warn('[AudioPlayer] File may not be ready, checking status...');

          // Re-check the file status
          if (currentTrackIdRef.current) {
            const fileId = currentTrackIdRef.current;
            getAudioFile(fileId).then(fileData => {
              const status = !fileData.processingStatus || !fileData.url ? 'pending' : fileData.processingStatus;

              if (status === 'pending' || status === 'processing') {
                console.log('[AudioPlayer] File is still processing, switching to processing mode');
                setIsProcessing(true);
                isProcessingRef.current = true;
                setError(null);
                audio.src = ''; // Clear the src to stop error events

                // Start polling
                loadTrack(fileId, false, true);
              } else {
                // File claims to be ready but won't play - show error
                let errorMsg = 'Failed to play audio. The file may not be ready yet or is corrupted.';
                if (errorCode === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                  errorMsg = 'Audio file format is not supported by your browser. The file may still be processing.';
                } else if (errorCode === MediaError.MEDIA_ERR_DECODE) {
                  errorMsg = 'Error decoding audio file. The file may still be processing or is corrupted.';
                }
                setError(errorMsg);
              }
            }).catch(err => {
              console.error('[AudioPlayer] Failed to re-check file status:', err);
              setError('Failed to load audio file. Please refresh and try again.');
            });

            setIsPlaying(false);
            return;
          }
        }

        // Provide more specific error messages based on error code
        let errorMsg = 'Failed to play audio. The file may be corrupted or unavailable.';
        if (errorCode === MediaError.MEDIA_ERR_NETWORK) {
          errorMsg = 'Network error loading audio. Please check your connection and try again.';
        } else if (errorCode === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          errorMsg = 'Audio format not supported by your browser.';
        } else if (errorCode === MediaError.MEDIA_ERR_DECODE) {
          errorMsg = 'Error decoding audio file. The file may be corrupted.';
        }
        setError(errorMsg);
      }
      setIsPlaying(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [loadTrack]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, []);

  const hasNext = queueIndex >= 0 && queueIndex < queue.length - 1;
  const hasPrevious = queueIndex > 0;

  // ---- Media Session API: lockscreen / Control Center now-playing + transport ----
  // Independent of Web Audio (no AudioContext routing), so it's safe for the
  // background-playback fix. Gives the OS the track info and wires the lockscreen
  // play/pause/next/prev buttons to our queue.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    ms.setActionHandler('play', () => play());
    ms.setActionHandler('pause', () => pause());
    // Only surface next/prev on the lockscreen when a playlist makes them real.
    ms.setActionHandler('nexttrack', hasNext ? () => playNext() : null);
    ms.setActionHandler('previoustrack', hasPrevious ? () => playPrevious() : null);
  }, [play, pause, playNext, playPrevious, hasNext, hasPrevious]);

  // Re-assert on isPlaying too: iOS sometimes only picks up metadata once the
  // session is actually active (right after play()), not at the moment we set it.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!currentTrack) {
      navigator.mediaSession.metadata = null;
      return;
    }
    // Re-assert the metadata on track change / play-pause (covers in-app title or
    // artwork edits). Artwork: 320² thumb → full cover → brand icon (see buildArtwork).
    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.title || currentTrack.filename || 'PasteWaves',
      artist: currentTrack.ownerName || 'PasteWaves',
      artwork: buildArtwork(currentTrack, collectionArtworkRef.current),
    });
  }, [currentTrack, isPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  const value: AudioPlayerContextType = {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isLoading,
    isProcessing,
    error,
    isFileNotFound,
    needsUserInteraction,
    loadingTrackId,
    waveformData,
    isGeneratingWaveform,
    loadTrack,
    play,
    pause,
    togglePlayPause,
    seek,
    setVolume,
    closePlayer,
    patchCurrentTrack,
    audioRef,
    getFrequencies,
    queue,
    queueIndex,
    hasNext,
    hasPrevious,
    playQueue,
    playNext,
    playPrevious,
    clearQueue,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {/* Hidden audio element managed globally. crossOrigin enables the realtime
          analyser (uploads CDN serves access-control-allow-origin: *) without
          tainting/muting the stream. */}
      <audio ref={audioRef} preload="metadata" crossOrigin="anonymous" />
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};
